import { forwardRef, ReactNode } from 'react';

import { cn } from '@crossnokaye/ux-common';

interface SidebarTemplateProps {
  /**
   * Content for the left sidebar
   */
  leftColumn: ReactNode;

  /**
   * Content for the main area
   */
  children: ReactNode;

  /**
   * Additional className for the container
   */
  className?: string;
}

/**
 * A reusable two-column layout component with responsive behavior.
 *
 * Features:
 * - Left sidebar that can be hidden on smaller screens
 * - Responsive breakpoints for mobile/desktop views
 * - Optional mobile positioning of left column content
 * - Customizable gaps, padding, and styling
 *
 * @example
 * ```tsx
 * <SidebarTemplate
 *   leftColumn={<MySidebar />}
 * >
 *   <MyMainContent />
 * </SidebarTemplate>
 * ```
 */
export const SidebarTemplate = forwardRef<HTMLDivElement, SidebarTemplateProps>(
  ({ leftColumn, children, className }, ref) => {
    return (
      <div className={cn('@container/sidebar flex w-full h-full relative', className)} ref={ref}>
        {/* Left sidebar - visible on larger screens, scrolls independently */}
        <div className="hidden @2xl:block flex-shrink-0 overflow-y-auto">{leftColumn}</div>

        {/* Main content container */}
        <div className={'@container flex flex-col w-full min-w-0 h-full'}>
          {/* Mobile version of left column content - visible on smaller screens */}
          <div className={'block @2xl/sidebar:hidden'}>{leftColumn}</div>

          {/* Main content - scrolls independently */}
          <div className="flex flex-col flex-1 overflow-y-auto min-h-0 @2xl:ml-4">{children}</div>
        </div>
      </div>
    );
  }
);

SidebarTemplate.displayName = 'SidebarTemplate';
